import React, { useState, useEffect, useRef, useContext } from 'react'
import gql from "graphql-tag"
import { MentionsInput, Mention } from 'react-mentions'
import { useQuery } from '@apollo/client'
import { Link, useSearchParams } from "react-router-dom"
import ReactMarkdown from "react-markdown"
import { Divider } from "@material-ui/core"
import { Nav, NavItem, NavLink, TabContent, TabPane, Form, Input, FormGroup, Button, Label, Card, CardHeader, CardBody, CardText, CardFooter, Row, Col, Modal, ModalHeader, ModalBody } from "reactstrap"
import Select from 'react-select'
import Skeleton from "react-loading-skeleton"
import moment from 'moment-timezone'
import RepAvatar from '../Material/RepAvatar'
import { RepsQuery, TaskQuery, AllTaskCommentsQuery, TaskStatusOptionsQuery } from "./Queries"
import { CreateTaskCommentMutation, CreateTaskChecklistMutation, UpdateTaskChecklistMutation, UpdateTaskMutation } from "./Mutations"
import withApolloClient from "../withApolloClient"
import "./TaskDetailsSidePanel.css"
import DatetimeRenderer from "../Material/DatetimeRenderer"
import TaskArchievePopover from './TaskComponents/TaskArchievePopover'
import EditCommentPopover from './TaskComponents/EditCommentPopover'
import ReactionsPopup from "./TaskComponents/ReactionsPopup"
import DocumentViewer from '../Material/DocumentViewer'
import HistoryList from '../Material/HistoryList'
import { RepsTypeAhead } from './TaskComponents/TypeAheadComponents'
import { CurrentUserContext } from '../Context/CurrentUserContext'
import { ForeignKeyInput, renderCustomColumn } from "./TaskComponents/ForeignKeyComponents"


const UpdateTaskCommentMutation = gql`
  mutation UpdateTaskComment($input: UpdateTaskCommentMutationInput!) {
    updateTaskComment(input: $input) {
      ok
      errors {
        messages
      }
    }
  }
`

function TaskDetailsSidePanel({ ...props }) {
    const { currentUser } = useContext(CurrentUserContext)
    const [searchParams, setSearchParams] = useSearchParams()
    const taskId = searchParams.get('task_id')
    const [activeTab, setActiveTab] = useState("#Updates")
    const [newChecklistTitle, setNewChecklistTitle] = useState("")
    const [panelError, setPanelError] = useState("")

    const { loading: taskLoading, error, data, refetch: refetchTaskQuery } = useQuery(TaskQuery, {
        variables: { id: taskId },
        fetchPolicy: 'cache-and-network',
        skip: !taskId, // Skip the query if taskId is not available
    })
    const task = data?.task

    const { loading: commentsLoading, error: commentsError, data: commentsData, refetch: refetchTaskCommentsQuery } = useQuery(AllTaskCommentsQuery, {
        variables: { taskId: taskId },
        fetchPolicy: 'cache-and-network',
        skip: !taskId,
    })
    const taskCommentsData = commentsData?.allTaskComments
    const [showEditIcon, setShowEditIcon] = useState(false)
    const [isEditingTitle, setIsEditingTitle] = useState(false)
    const [editedTitle, setEditedTitle] = useState(task?.title || "")
    const [isEditingDescription, setIsEditingDescription] = useState(false)
    const [editedDescription, setEditedDescription] = useState("")
    const [showEditDescriptionIcon, setShowEditDescriptionIcon] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const refetchTaskData = async () => {
        if (refetchTaskCommentsQuery) {
            await refetchTaskCommentsQuery()
        }
        if (refetchTaskQuery) {
            await refetchTaskQuery()
        }
    }

    const toggleChecklistItem = (checklistId, value) => {
        try {
            const input = {
                checklistId: checklistId, isCompleted: value,
            }

            props.apolloClient.mutate({
                mutation: UpdateTaskChecklistMutation,
                variables: { input },
            }).then((result) => {
                if (result && result.data && result.data.updateTaskChecklist && result.data.updateTaskChecklist.ok) {
                    setPanelError("")
                    refetchTaskData()
                }
                if (!result || !result.data || !result.data.updateTaskChecklist || !result.data.updateTaskChecklist.ok) { throw new Error("Error updating checklist item") }
            }).catch((error) => {
                setPanelError("Error updating checklist item: " + error)
            })
        } catch (error) {
            setPanelError("Error updating checklist item :" + error)
        }
    }

    const createChecklist = () => {
        try {
            const input = {
                taskId: task.id,
                title: newChecklistTitle,
            }

            props.apolloClient.mutate({
                mutation: CreateTaskChecklistMutation,
                variables: { input },
            }).then((result) => {
                if (result && result.data && result.data.createTaskChecklist && result.data.createTaskChecklist.ok) {
                    setPanelError("")
                    refetchTaskData()
                }
                if (!result || !result.data || !result.data.createTaskChecklist || !result.data.createTaskChecklist.ok) { throw new Error("Error creating checklist item") }
                setNewChecklistTitle("")
            }).catch((error) => {
                setPanelError("Error creating checklist item :" + error)
            })
        } catch (error) {
            setPanelError("Error creating checklist item :" + error)
        }
    }

    if (taskLoading && !task) {
        return (
            <div className="TaskDetailsSidePanelContainer">
                <div className="TaskDetailsSidePanel show">
                    <Skeleton height={50} width={200} />
                    <Skeleton count={3} height={30} />
                    <Skeleton height={150} />
                    <Skeleton count={2} height={20} />
                </div>
            </div>
        )
    }

    const handleSaveTaskField = async (field, value) => {
        if (!field) return
        setIsSaving(true)

        const input = {
            taskId,
            [field]: value,
        }

        try {
            const result = await props.apolloClient.mutate({
                mutation: UpdateTaskMutation,
                variables: { input },
            })

            if (result?.data?.updateTask?.ok) {
                setPanelError("")
                await refetchTaskData()
            } else if (result?.data?.updateTask?.errors[0]?.messages) {
                setPanelError(result.data.updateTask.errors[0].messages.toString())
                return
            } else {
                setPanelError(`Error updating task ${field}.`)
                return
            }

            if (field === "title") setIsEditingTitle(false)
            if (field === "description") setIsEditingDescription(false)
        } catch (error) {
            setPanelError(error?.message || `Error updating task ${field}.`)
        }
        finally {
            setIsSaving(false)
        }
    }

    const clearTaskId = (e) => {
        e.preventDefault()
        const next = new URLSearchParams(searchParams)
        next.delete('task_id')
        setSearchParams(next, { replace: true }) // keep other params, replace history
    }

    const history_item_ids = [task?.id || taskId]
    if (taskCommentsData?.edges)
        history_item_ids.push(...taskCommentsData.edges.map(edge => edge.node.id))
    return (
        <div>
            <div className="TaskDetailsSidePanelContainer" >
                <div className={`TaskDetailsSidePanel show`}>
                    {/* on click of the close button, go back to the previous page */}
                    {/* <Link to={`/boards${boardId ? "/" + boardId : ""}/tasks/`} className="btn btn-outline-dark"> */}
                    <div className="TaskDetailsSidePanelHeader d-flex justify-content-between align-items-center mb-3">
                        <Link onClick={clearTaskId} className="close-button">
                            <i className="fa fa-times mb-3" aria-hidden="true"></i>
                        </Link>
                        {!task?.isAutomated && <TaskArchievePopover taskId={task?.id} onArchived={clearTaskId} />}
                    </div>
                    {(error || commentsError || panelError) && (
                        <div className="alert alert-danger">
                            {error?.message || commentsError?.message || panelError}
                        </div>
                    )}
                    <div className="editable-title-container d-inline-flex align-items-center" onMouseEnter={() => !task?.isAutomated && setShowEditIcon(true)} onMouseLeave={() => { setShowEditIcon(false); if (!task?.isAutomated && isEditingTitle) handleSaveTaskField("title", editedTitle) }}>
                        {!isEditingTitle || task?.isAutomated ? (
                            <>
                                <h3 className="task-title">{task?.title || "Untitled"}</h3>

                                {showEditIcon && !task?.isAutomated && (
                                    <span className="edit-icon-box ml-2" onClick={() => { setEditedTitle(task?.title || ""); setIsEditingTitle(true) }}><i className="fa fa-pencil-square-o"></i></span>
                                )}
                            </>
                        ) : (
                            <div className="d-flex align-items-center">
                                <input type="text" className="form-control title-edit-input" value={editedTitle} autoFocus onChange={(e) => setEditedTitle(e.target.value)} onBlur={() => handleSaveTaskField("title", editedTitle)} onKeyDown={(e) => e.key === "Enter" && handleSaveTaskField("title", editedTitle)} />
                                {isSaving && <span className="ml-2"><i className="fa fa-spinner fa-spin"></i></span>}
                            </div>
                        )}
                    </div>

                    <br />

                    <div className="editable-title-container d-inline-flex align-items-start" onMouseEnter={() => setShowEditDescriptionIcon(true)} onMouseLeave={() => { setShowEditDescriptionIcon(false); if (isEditingDescription) handleSaveTaskField("description", editedDescription) }}>
                        {!isEditingDescription ? (
                            <>
                                <p className="task-description mb-0">{task?.description || "No description"}</p>

                                {showEditDescriptionIcon && (
                                    <span className="edit-icon-box ml-2" onClick={() => { setEditedDescription(task?.description || ""); setIsEditingDescription(true) }}><i className="fa fa-pencil-square-o"></i></span>
                                )}
                            </>
                        ) : (
                            <div className="d-flex align-items-center">
                                <textarea className="form-control title-edit-input" style={{ width: "500px" }} value={editedDescription} autoFocus onChange={(e) => setEditedDescription(e.target.value)} onBlur={() => handleSaveTaskField("description", editedDescription)} onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) handleSaveTaskField("description", editedDescription) }} />
                                {isSaving && <span className="ml-2"><i className="fa fa-spinner fa-spin"></i></span>}
                            </div>
                        )}
                    </div>
                    <Nav tabs className="mt-4">
                        <NavItem className={activeTab === "#Updates" ? "active" : "inactive"}><NavLink onClick={() => setActiveTab("#Updates")} active={activeTab === "#Updates"} style={{ cursor: "pointer" }}>Updates</NavLink></NavItem>
                        <NavItem className={activeTab === "#Fields" ? "active" : "inactive"}><NavLink onClick={() => setActiveTab("#Fields")} active={activeTab === "#Fields"} style={{ cursor: "pointer" }}>Fields</NavLink></NavItem>
                        <NavItem className={activeTab === "#History" ? "active" : "inactive"}><NavLink onClick={() => setActiveTab("#History")} active={activeTab === "#History"} style={{ cursor: "pointer" }}>History</NavLink></NavItem>
                    </Nav>
                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="#Updates">
                            <NewCommentForm commentId={task?.id} refetch={refetchTaskData} {...props} />
                            <Divider className="my-4" />
                            {commentsLoading && <Skeleton height={24} count={3} />}
                            {taskCommentsData && taskCommentsData?.edges && taskCommentsData?.edges?.map((comment, index) => {
                                return <CommentCard
                                    key={index}
                                    comment={comment.node}
                                    currentUsername={currentUser?.username}
                                    refetch={refetchTaskData}
                                    {...props} />
                            })}
                        </TabPane>
                        <TabPane tabId="#Fields">
                            <TaskFieldsTab task={task} apolloClient={props.apolloClient} refetch={refetchTaskData} />
                        </TabPane>
                        <TabPane tabId="#History">
                            <HistoryList objectIds={history_item_ids} />
                        </TabPane>
                        <TabPane tabId="#Checklist">
                            <ul className="list-unstyled">
                                {task && task?.taskchecklistSet && task?.taskchecklistSet.edges && task?.taskchecklistSet.edges.length > 0 &&
                                    task?.taskchecklistSet.edges.map((checklist, index) => (
                                        checklist = checklist.node,
                                        <li key={index} className="mb-2">
                                            <Row className="align-items-center">
                                                <Col className="text-left">
                                                    <FormGroup check>
                                                        <Input
                                                            id={checklist.id}
                                                            type="checkbox"
                                                            checked={checklist.completedOn}
                                                            onChange={() => toggleChecklistItem(checklist.id, !checklist.completedOn)}
                                                        />
                                                        {' '}
                                                        <Label check for={checklist.id}>
                                                            <span style={{ textDecoration: checklist.completedOn ? "line-through" : "none" }}>{checklist.title}</span>
                                                        </Label>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </li>
                                    ))}
                                {/* New checklist input text with submit button on the right */}
                                <li className="mt-4">
                                    <Row className="align-items-center">
                                        <Col className="text-left">
                                            <FormGroup>
                                                <Input
                                                    value={newChecklistTitle}
                                                    onChange={(e) => setNewChecklistTitle(e.target.value)}
                                                    type="text" name="checklist"
                                                    id="checklist"
                                                    placeholder="Add a new checklist item"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col className="text-right">
                                            <Button onClick={createChecklist} color="primary">Add</Button>
                                        </Col>
                                    </Row>
                                </li>
                            </ul>
                        </TabPane>
                    </TabContent>

                </div>
            </div>
        </div>
    )
}


function TaskFieldsTab({ task, apolloClient, refetch }) {
    const [editingField, setEditingField] = useState(null) // field key being edited
    const [editValue, setEditValue] = useState(null)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(null)
    const { data: taskStatusData } = useQuery(TaskStatusOptionsQuery)

    const customColumns = task?.board?.columnSet?.edges
        ?.filter(({ node }) => !node.isArchived)
        .map(({ node }) => node) || []

    const valueMap = {}
    task?.taskcolumnvalueSet?.edges?.forEach(({ node }) => {
        valueMap[node.column.id] = node
    })
    const statusOptions = (taskStatusData?.optionsList?.enumValues || []).map((status) => ({
        value: status.name,
        label: status.description || status.name,
    }))
    const filteredStatusOptions = task?.isAutomated
        ? statusOptions.filter(({ value }) => !["DONE", "CANCELED", "CANCELLED"].includes(value))
        : statusOptions
    const canEditCustomFields = !task?.isAutomated

    const startEdit = (fieldKey, initialValue) => {
        setEditingField(fieldKey)
        setEditValue(initialValue)
        setError(null)
    }

    const cancelEdit = () => {
        setEditingField(null)
        setEditValue(null)
    }

    const saveStandardField = async (fieldKey, value) => {
        setSaving(true)
        setError(null)
        try {
            const result = await apolloClient.mutate({
                mutation: UpdateTaskMutation,
                variables: { input: { taskId: task.id, [fieldKey]: value } },
                refetchQueries: ['AllTasks'],
            })
            if (!result?.data?.updateTask?.ok) {
                const msg = result?.data?.updateTask?.errors?.[0].messages?.[0] || 'Failed to save'
                setError(msg)
            }
            if (refetch) await refetch()
            setEditingField(null)
        } catch (e) {
            setError(e.message || 'Failed to save')
        } finally {
            setSaving(false)
        }
    }

    const saveCustomColumn = async (col) => {
        if (!canEditCustomFields) {
            setError("Only status, due date and assigned to are editable for automated tasks.")
            return
        }
        setSaving(true)
        setError(null)
        try {
            const result = await apolloClient.mutate({
                mutation: UpdateTaskMutation,
                variables: { input: { taskId: task.id, columnValues: [{ columnId: col.id, value: editValue || '' }] } },
                refetchQueries: ['AllTasks'],
            })
            if (!result?.data?.updateTask?.ok) {
                const msg = result?.data?.updateTask?.errors?.[0].messages?.[0] || 'Failed to save'
                throw new Error(msg)
            }
            if (refetch) await refetch()
            setEditingField(null)
        } catch (e) {
            setError(e.message || 'Failed to save')
        } finally {
            setSaving(false)
        }
    }

    const renderSaveCancel = (onSave) => (
        <div className="mt-1 d-flex">
            <Button size="sm" color="primary" onClick={onSave} disabled={saving}>
                {saving ? <i className="fa fa-spinner fa-spin" /> : 'Save'}
            </Button>
            <Button size="sm" color="secondary" className="ml-1" onClick={cancelEdit} disabled={saving}>Cancel</Button>
        </div>
    )

    const renderCustomColInput = (col) => {
        if (col.columnType === 'MIRROR') {
            return null
        }
        if (col.columnType === 'ENUM') {
            const options = (col.allowedOptions || []).map(opt => ({ value: opt, label: opt }))
            return (
                <Select
                    options={options}
                    value={editValue ? { value: editValue, label: editValue } : null}
                    onChange={(s) => setEditValue(s ? s.value : '')}
                    isClearable
                    autoFocus
                />
            )
        }
        if (col.columnType === 'FK_OBJECT') {
            return <ForeignKeyInput model={col.contentType?.model} onChange={setEditValue} defaultValue={valueMap[col.id]?.value} />
        }
        if (col.columnType === 'DATE') {
            return <Input type="date" value={editValue || ''} onChange={(e) => setEditValue(e.target.value)} autoFocus />
        }
        return (
            <Input
                type="text"
                value={editValue || ''}
                autoFocus
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveCustomColumn(col) }}
            />
        )
    }

    const FieldRow = ({ label, displayValue, fieldKey, onEdit, children }) => {
        const isEditing = editingField === fieldKey
        return (
            <div className="task-field-row">
                <div className="task-field-label">{label}</div>
                <div className="task-field-value">
                    {isEditing ? (
                        <div>{children}</div>
                    ) : (
                        <div className="d-flex align-items-center task-field-display">
                            <span className={displayValue ? '' : 'text-muted'}>{displayValue || '—'}</span>
                            {onEdit && (
                                <span className="edit-icon-box ml-2" onClick={onEdit}>
                                    <i className="fa fa-pencil-square-o" />
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="task-fields-tab mt-3">
            {error && <div className="alert alert-danger py-1 mb-2">{error}</div>}

            {/* Standard task fields */}
            <FieldRow
                label="Status"
                fieldKey="status"
                displayValue={task?.status}
                onEdit={() => startEdit('status', task?.status || '')}
            >
                <Select
                    options={filteredStatusOptions}
                    value={filteredStatusOptions.find(o => o.value === editValue) || null}
                    onChange={(s) => setEditValue(s ? s.value : '')}
                    autoFocus
                />
                {renderSaveCancel(() => saveStandardField('status', editValue))}
            </FieldRow>

            <FieldRow
                label="Due Date"
                fieldKey="dueDate"
                displayValue={task?.dueDate ? moment(task.dueDate).format('MMM D, YYYY') : null}
                onEdit={() => startEdit('dueDate', task?.dueDate ? task.dueDate.split('T')[0] : '')}
            >
                <Input type="date" value={editValue || ''} onChange={(e) => setEditValue(e.target.value)} autoFocus />
                {renderSaveCancel(() => saveStandardField('dueDate', editValue))}
            </FieldRow>

            <FieldRow
                label="Assigned To"
                fieldKey="assignedTo"
                displayValue={task?.assignedTo?.edges?.length > 0
                    ? task.assignedTo.edges.map(({ node: u }) => u.firstName ? `${u.firstName} ${u.lastName}` : u.username).join(', ')
                    : null}
                onEdit={() => startEdit('assignedTo', task?.assignedTo?.edges?.map(({ node }) => node.id) || [])}
            >
                <RepsTypeAhead
                    defaultValue={task?.assignedTo?.edges?.map(({ node }) => node.id) || []}
                    onChange={(ids) => setEditValue(ids)}
                />
                {renderSaveCancel(() => saveStandardField('assignedToIds', editValue || []))}
            </FieldRow>

            <FieldRow
                label="Date Added"
                fieldKey="dateAdded"
                displayValue={task?.dateAdded ? moment(task.dateAdded).format('MMM D, YYYY h:mm A') : null}
            />

            <FieldRow
                label="Created By"
                fieldKey="createdBy"
                displayValue={task?.createdBy
                    ? (task.createdBy.firstName ? `${task.createdBy.firstName} ${task.createdBy.lastName}` : task.createdBy.username)
                    : null}
            />

            {/* Custom board columns */}
            {customColumns.map(col => {
                const currentValNode = valueMap[col.id]
                const displayValue = currentValNode?.valueString
                const isMirrorColumn = col.columnType === 'MIRROR'
                const isEditing = canEditCustomFields && !isMirrorColumn && editingField === `col_${col.id}`
                return (
                    <div key={col.id} className="task-field-row">
                        <div className="task-field-label">{col.title}</div>
                        <div className="task-field-value">
                            {isEditing ? (
                                <div>
                                    {renderCustomColInput(col)}
                                    {renderSaveCancel(() => saveCustomColumn(col))}
                                </div>
                            ) : (
                                <div className="d-flex align-items-center task-field-display">
                                    {renderCustomColumn(currentValNode)}
                                    {canEditCustomFields && !isMirrorColumn && (
                                        <span className="edit-icon-box ml-2" onClick={() => startEdit(`col_${col.id}`, displayValue || '')}>
                                            <i className="fa fa-pencil-square-o" />
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export function CommentCard({ comment, currentUsername, refetch, ...props }) {
    const [isEditing, setIsEditing] = useState(false)
    const [editedComment, setEditedComment] = useState(comment.comment)
    const [saving, setSaving] = useState(false)
    const [previewUrl, setPreviewUrl] = useState(null)
    const [previewType, setPreviewType] = useState(null)
    const canEditComment = currentUsername && currentUsername === comment?.createdBy?.username

    if (!comment) {
        return null
    }

    const handleCommentEdit = (fullComment) => {
        if (!fullComment) return
        setEditedComment(fullComment.comment)   // keep old value
        setIsEditing(true)
    }

    const handleSave = async () => {
        try {
            setSaving(true)

            const input = {
                commentId: comment.id,
                comment: editedComment,
            }

            await props.apolloClient.mutate({
                mutation: UpdateTaskCommentMutation,
                variables: { input },
            })
            if (refetch) {
                await refetch()
            }

            setIsEditing(false)
        } catch (e) {
            console.log("Save error", e)
        } finally {
            setSaving(false)
        }
    }

    return (
        <Card className="my-4 CommentCard">
            <CardHeader>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <span className="text-secondary"><RepAvatar rep={comment.createdBy} keepOpen /></span>
                    </div>
                    <div><DatetimeRenderer datetime={comment.dateAdded} /></div>
                </div>
            </CardHeader>
            <CardBody>
                {isEditing ? (
                    <div>
                        <textarea className="form-control" value={editedComment} rows={4} onChange={(e) => setEditedComment(e.target.value)} />
                        <div className="mt-3 d-flex gap-2">
                            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</button>&nbsp;&nbsp;
                            <button className="btn btn-secondary btn-sm" onClick={() => {
                                setIsEditing(false)
                                setEditedComment(comment.comment)
                            }}>Cancel</button>
                        </div>
                    </div>
                ) : (
                    <CardText className='font'>
                        <ReactMarkdown children={comment.comment} />
                    </CardText>
                )}

                {comment.taskcommentattachmentSet && comment.taskcommentattachmentSet.edges && comment.taskcommentattachmentSet.edges.length > 0 && (
                    <div>
                        <Divider className="my-3" />
                        <div className="attachment-row">
                            {comment.taskcommentattachmentSet.edges.map((item, index) => {
                                let attachment = item.node
                                const openPreview = (type) => {
                                    setPreviewUrl(attachment.url)
                                    setPreviewType(type)
                                }
                                return (
                                    <div key={index} className="attachment-box" onClick={() => openPreview(["png", "jpeg", "jpg", "tiff", "webp", "gif"].includes(attachment.extension) ? "image" : ["mp4"].includes(attachment.extension) ? "video" : ["mp3"].includes(attachment.extension) ? "audio" : ["pdf"].includes(attachment.extension) ? "pdf" : null)}                                    >
                                        <div className="attachment-name">{attachment.name.length > 20 ? attachment.name.substring(0, 20) + "..." : attachment.name}</div>
                                        {["png", "jpeg", "jpg", "tiff", "webp", "gif"].includes(attachment.extension) && (
                                            <img className="attachment-thumbnail" src={attachment.url} alt={attachment.name} />
                                        )}
                                        {["mp4"].includes(attachment.extension) && (
                                            <video className="attachment-thumbnail" src={attachment.url} />
                                        )}

                                        {["mp3"].includes(attachment.extension) && (
                                            <audio className="attachment-audio" src={attachment.url} controls />
                                        )}

                                        {["pdf"].includes(attachment.extension) && (
                                            <div className="attachment-pdf"><i className="fa fa-file-pdf-o fa-3x text-danger"></i></div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                <Modal isOpen={!!previewUrl} toggle={() => setPreviewUrl(null)} size="lg">
                    <ModalHeader toggle={() => setPreviewUrl(null)}>Preview</ModalHeader>
                    <ModalBody>
                        {{
                            image: <DocumentViewer documentURL={previewUrl} handleClose={() => setPreviewUrl(null)} documentName={"Uploaded Document"} />,
                            video: <video src={previewUrl} style={{ width: "100%" }} controls />,
                            audio: <audio src={previewUrl} style={{ width: "100%" }} controls />,
                            pdf: <iframe src={previewUrl} style={{ width: "100%", height: "80vh" }} />
                        }[previewType] || null}
                    </ModalBody>
                </Modal>
            </CardBody>
            <CardFooter>
                {/* Like, reply buttons  */}
                <div className="d-flex justify-content-between align-items-center">
                    <ReactionsPopup commentId={comment.id} />
                    <EditCommentPopover comment={comment} canEdit={canEditComment} onEdit={handleCommentEdit} refetch={refetch} />
                </div>
            </CardFooter>
        </Card>
    )
}

export function NewCommentForm({ commentId, refetch, ...props }) {
    const [commentBody, setCommentBody] = useState("")
    const [error, setError] = useState("")
    const [loading, setLoading] = useState(false)
    const [files, setFiles] = useState([])

    let inp = useRef()
    useEffect(() => {
        if (!inp && !inp.current) return
        // inp.current.focus()
        return () => inp = null
    })

    // Submit when user presses Shift + Enter
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Enter" && e.shiftKey) {
                createTaskComment(e)
            }
        }
        document.addEventListener("keydown", handleKeyDown)
        return () => document.removeEventListener("keydown", handleKeyDown)
    }, [commentBody])

    // Find users with RepsQuery
    const searchReps = async (search, callBack) => {
        try {
            if (!props.apolloClient) {
                throw new Error("GQL Client not found")
            }

            props.apolloClient.query({
                query: RepsQuery,
                variables: { searchTerm: search },
            }).then((result) => {
                if (!result) {
                    throw new Error("Error loading reps")
                }
                if (result.data && result.data.allReps && result.data.allReps.edges) {
                    let reps = result.data.allReps.edges.map(rep => { return { id: rep.node.id, display: rep.node.firstName ? rep.node.firstName + " " + rep.node.lastName : rep.node.username } })
                    callBack(reps)
                } else {
                    console.log("Error loading reps", result.data.allReps)
                }
            }).catch((error) => {
                console.log("Error loading reps", error)
            })
        } catch (error) {
            console.log("Error loading reps", error)
        }
    }


    const handlePaste = (e) => {
        const clipboard = e.clipboardData
        if (!clipboard) return

        const pastedFiles = Array.from(clipboard.files || []).length > 0
            ? Array.from(clipboard.files || [])
            : Array.from(clipboard.items || [])
                .filter((item) => item.kind === "file")
                .map((item) => item.getAsFile())
                .filter(Boolean)

        const pastedImages = pastedFiles.filter((file) => file?.type?.startsWith("image/"))
        if (!pastedImages.length) return

        const normalizedFiles = pastedImages.map((file, index) => {
            if (file.name) return file
            const extension = file?.type?.split("/")?.[1] || "png"
            return new File([file], `pasted-file-${Date.now()}-${index}.${extension}`, { type: file.type })
        })

        setFiles((prev) => {
            const merged = [...prev, ...normalizedFiles]
            return merged.filter((file, index, arr) =>
                arr.findIndex((f) =>
                    f.size === file.size &&
                    f.type === file.type &&
                    f.lastModified === file.lastModified
                ) === index
            )
        })
    }

    const removeFileAtIndex = (indexToRemove) => {
        setFiles((prev) => prev.filter((_, index) => index !== indexToRemove))
    }

    const createTaskComment = async (e) => {
        e.preventDefault()
        try {
            if (!props.apolloClient || !commentId) {
                throw new Error("GQL Client not found")
            }
            if (!commentBody || commentBody.length < 5) {
                setError("Comment is required or too short")
                return
            }
            setLoading(true)
            let filesInBase64 = [] // {name: "", type: "", data: ""}
            for (let i = 0; i < files.length; i++) {
                const file = files[i]
                // Convert file to base64
                const reader = new FileReader()
                reader.readAsDataURL(file)
                await new Promise((resolve, reject) => {
                    reader.onload = () => {
                        filesInBase64.push({ name: file.name, type: file.type, data: reader.result })
                        resolve()
                    }
                    reader.onerror = error => reject(error)
                })
            }

            const input = {
                taskId: commentId,
                comment: commentBody,
                files: filesInBase64
            }

            props.apolloClient.mutate({
                mutation: CreateTaskCommentMutation,
                variables: { input },
            }).then((result) => {
                setLoading(false)
                if (!result) {
                    throw new Error("Error creating task update")
                }
                if (result.data && result.data.createTaskComment && result.data.createTaskComment.ok) {
                    setError("")
                    setCommentBody("")
                    setFiles([])
                    if (refetch) {
                        refetch()
                    }

                } else {
                    const errorMessage = result.data && result.data.createTaskComment && result.data.createTaskComment.errors && result.data.createTaskComment.errors.messages ? result.data.createTaskComment.errors.messages[0] : "Unknown Error"
                    throw new Error(errorMessage)
                }
            }).catch((error) => {
                setError("Error creating task update" + error.toString())
                setLoading(false)
            })
        } catch (error) {
            console.log("Error creating task update", error)
            setError("Error creating task update" + error.toString())
            setLoading(false)
        }
    }

    return (
        <Form className="NewCommentForm" name="NewCommentForm" onSubmit={createTaskComment}>
            <FormGroup className="NewCommentTextAreaFormGroup">
                {/* <Label for="exampleText">Write an update</Label> */}
                <div className="MentionsInputContainer position-relative">
                    <MentionsInput placeholder="Write an update and mention someone with @" value={commentBody} onChange={(e) => setCommentBody(e.target.value)} onPaste={handlePaste} className="text-area" inputRef={inp}>
                        <Mention
                            trigger="@"
                            data={(search, callBack) => searchReps(search, callBack)}
                            displayTransform={(id, display) => `@${display}`}
                            appendSpaceOnAdd={true}
                            renderSuggestion={(suggestion, search, highlightedDisplay) => (
                                <div className="user-suggestion">
                                    {highlightedDisplay}
                                </div>
                            )}
                        />
                    </MentionsInput>

                    <button type="button" className="pin-icon" title="Attach a file" aria-label="Attach a file" onClick={() => document.getElementById("hiddenFileInput").click()}><i className="fa fa-paperclip" aria-hidden="true"></i></button>
                    <input type="file" id="hiddenFileInput" style={{ display: "none" }} multiple onChange={(e) => setFiles([...e.target.files])} />
                </div>

            </FormGroup>
            {error && <div className="text-danger mb-2">{error}</div>}
            <div>
                {files && files.length > 0 && (
                    <div className="d-flex flex-wrap gap-3 mt-2">
                        {files.map((file, index) => {
                            const url = URL.createObjectURL(file)

                            return (
                                <div key={index} className="p-2 border rounded d-flex flex-column align-items-center" style={{ width: "120px", position: "relative" }}>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light attachment-remove-button"
                                        onClick={() => removeFileAtIndex(index)}
                                        aria-label={`Remove ${file.name}`}
                                        title="Remove attachment"
                                    >
                                        ×
                                    </button>
                                    {/* File name (truncate) */}
                                    <div className="text-truncate w-100 small mb-1" title={file.name}>
                                        {file.name}
                                    </div>

                                    {/* IMAGE */}
                                    {file.type.includes("image") && (
                                        <img src={url} alt={file.name} style={{ width: "100%", height: "80px", objectFit: "cover" }} />
                                    )}

                                    {/* VIDEO */}
                                    {file.type.includes("video") && (
                                        <video src={url} style={{ width: "100%", height: "80px", objectFit: "cover" }} controls />
                                    )}

                                    {/* AUDIO */}
                                    {file.type.includes("audio") && (
                                        <audio src={url} controls style={{ width: "100%" }} />
                                    )}

                                    {/* PDF */}
                                    {file.type.includes("pdf") && (
                                        <iframe src={url} style={{ width: "100%", height: "80px", border: 0 }} title={file.name} />
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
            <div className="update-submit-button-container">
                <Button type="submit" disabled={!commentBody || loading || !commentId}>{loading ? "Adding update..." : "Add update"}</Button>
            </div>
        </Form>
    )
}

export default withApolloClient(TaskDetailsSidePanel)
